import React, { Suspense, useContext } from 'react'
import { Route, Routes } from 'react-router-dom'
import { useSentryDatabaseContext } from 'utils/sentry'

import AppManagementRouterRedirect from './AppManagementRouterRedirect'
import AppSettingsContext from './context'
import IngressDomainLoadingStatus from './IngressDomainLoadingStatus'
import AppAccessRouter from './tabs/access/AppAccessRouter'
import AppConnectRouter from './tabs/auth/AppConnectRouter'
import AppDevicesRouter from './tabs/devices/AppDevicesRouter'
import { AppSettingsRouter } from './tabs/settings'

const AppCollectionsScene = React.lazy(() => import('./tabs/collections'))
const AppMetricsScene = React.lazy(() => import('./tabs/metrics'))
const AppCDCRouter = React.lazy(() => import('./tabs/cdc'))

type Props = {
  // Current app ID
  appId: string
}

/** Handles the routes used for managing apps. */
export default function AppManagementRouter({ appId }: Props) {
  useSentryDatabaseContext()

  // Data gates come pre-computed (staff override baked in) from context so the
  // router always agrees with the nav. `defaultArea` reflects those gates.
  const {
    viewerCanAccessMetrics,
    allowStreams,
    allowCollections = false,
    allowDevices = false,
    allowAuth = false,
    allowAccess = false,
    defaultArea = 'settings',
  } = useContext(AppSettingsContext)

  return (
    <Suspense fallback={null}>
      <IngressDomainLoadingStatus />
      <Routes>
        <Route path="settings/*" element={<AppSettingsRouter />} />
        {allowCollections && (
          <Route
            path="collections"
            element={<AppCollectionsScene appId={appId} />}
          />
        )}
        {allowDevices && (
          <Route path="devices/*" element={<AppDevicesRouter />} />
        )}
        {viewerCanAccessMetrics && (
          <Route path="metrics" element={<AppMetricsScene />} />
        )}
        {allowStreams && <Route path="cdc/*" element={<AppCDCRouter />} />}
        {allowAuth && (
          <Route
            path="connect/*"
            element={<AppConnectRouter appId={appId} />}
          />
        )}
        {allowAccess && (
          <Route path="access/*" element={<AppAccessRouter appId={appId} />} />
        )}
        <Route
          path="*"
          element={<AppManagementRouterRedirect area={defaultArea} />}
        />
      </Routes>
    </Suspense>
  )
}
